/**
 * Service API pour la gestion des commissions
 */
import { api } from './api';
import type {
  CommissionConfig,
  CommissionConfigCreate,
  CommissionPeriod,
  CommissionDetail,
  CommissionValidateRequest,
  CommissionPayRequest,
  CommissionCancelRequest,
  CommissionCalculateRequest,
  CommissionDashboard,
  CommissionSuperAdminDashboard,
  CommissionCalculateResponse,
  CommissionStatus
} from '../types/commission';

// ==================== Configuration ====================

export const commissionService = {
  // Liste des configurations
  async listConfigs(params?: {
    admin_id?: number;
    is_active?: boolean;
  }): Promise<CommissionConfig[]> {
    const response = await api.get('/commissions/configs', { params });
    return response.data;
  },

  // Configuration active d'un admin
  async getActiveConfig(adminId: number): Promise<CommissionConfig> {
    const response = await api.get(`/commissions/configs/${adminId}/active`);
    return response.data;
  },

  // Créer/modifier une configuration
  async createConfig(data: CommissionConfigCreate): Promise<CommissionConfig> {
    const response = await api.post('/commissions/configs', data);
    return response.data;
  },

  // Désactiver une configuration
  async deactivateConfig(configId: number): Promise<void> {
    await api.delete(`/commissions/configs/${configId}`);
  },

  // ==================== Périodes de commission ====================

  // Liste des périodes
  async listPeriods(params?: {
    admin_id?: number;
    status?: CommissionStatus;
    year?: number;
    month?: number;
    skip?: number;
    limit?: number;
  }): Promise<CommissionPeriod[]> {
    const response = await api.get('/commissions/periods', { params });
    return response.data;
  },

  // Détails d'une période
  async getPeriod(periodId: number): Promise<CommissionPeriod> {
    const response = await api.get(`/commissions/periods/${periodId}`);
    return response.data;
  },

  // Détails des factures d'une période
  async getPeriodDetails(
    periodId: number,
    params?: { skip?: number; limit?: number }
  ): Promise<CommissionDetail[]> {
    const response = await api.get(`/commissions/periods/${periodId}/details`, { params });
    return response.data;
  },

  // Valider une période
  async validatePeriod(
    periodId: number,
    data: CommissionValidateRequest
  ): Promise<CommissionPeriod> {
    const response = await api.post(`/commissions/periods/${periodId}/validate`, data);
    return response.data;
  },

  // Marquer comme payée
  async payPeriod(
    periodId: number,
    data: CommissionPayRequest
  ): Promise<CommissionPeriod> {
    const response = await api.post(`/commissions/periods/${periodId}/pay`, data);
    return response.data;
  },

  // Annuler une période
  async cancelPeriod(
    periodId: number,
    data: CommissionCancelRequest
  ): Promise<CommissionPeriod> {
    const response = await api.post(`/commissions/periods/${periodId}/cancel`, data);
    return response.data;
  },

  // Supprimer une période
  async deletePeriod(periodId: number): Promise<void> {
    await api.delete(`/commissions/periods/${periodId}`);
  },

  // ==================== Calcul ====================

  // Déclencher le calcul
  async calculateCommissions(
    data: CommissionCalculateRequest
  ): Promise<CommissionCalculateResponse> {
    const response = await api.post('/commissions/calculate', data);
    return response.data;
  },

  // ==================== Dashboards ====================

  // Dashboard admin
  async getAdminDashboard(adminId: number): Promise<CommissionDashboard> {
    const response = await api.get(`/commissions/dashboard/admin/${adminId}`);
    return response.data;
  },

  // Dashboard super admin
  async getSuperAdminDashboard(): Promise<CommissionSuperAdminDashboard> {
    const response = await api.get('/commissions/dashboard/super-admin');
    return response.data;
  }
};

export default commissionService;
